export const en = {
  lang: "en" as const,
  langLabel: "EN",
  langFull: "English",
  switchTo: "Français",

  nav: {
    flightDeck: "Flight Deck",
    requestTakeoff: "Request Takeoff",
    scenarioTraining: "Scenario Training",
    turbulenceZones: "Turbulence Zones",
    flightLogbook: "Flight Logbook",
    tower: "Tower",
    adminPanel: "Admin Panel",
    questionBank: "Question Bank",
    questionReview: "Question Review",
    aiScenarios: "Scenario Training",
    supportTickets: "Support Tickets",
    modTeam: "Mod Team",
    settings: "Settings",
    signOut: "Sign out",
    signOutInstead: "Sign out instead",
    openMenu: "Open menu",
    openNav: "Open navigation menu",
    utcTime: "UTC time",
    utcTimeShort: "UTC",
    localTime: "Local time",
    localTimeShort: "Local",
    utcClockTitle:
      "Coordinated Universal Time (UTC). Same as Zulu time in aviation NOTAMs, weather, and flight plans.",
    localClockTitle: "Your device's local time zone.",
    cadorsDaily: "CADORS Daily",
  },

  twoFa: {
    title: "2-STEP VERIFICATION REQUIRED",
    body: "Admin and moderator accounts must have two-factor authentication enabled. You must set it up before accessing any features.",
    cta: "SET UP 2FA NOW",
  },

  signup: {
    readyToFly: "Ready to Fly",
    title: "FLIGHT DECK",
    subtitle: "EXAM PREP",
    tagline: "Create your account in seconds. You can fill in your aviation profile later from Settings.",
    fullName: "FULL NAME",
    email: "EMAIL",
    password: "PASSWORD",
    confirmPassword: "CONFIRM PASSWORD",
    showPassword: "Show password",
    hidePassword: "Hide password",
    passwordMismatch: "Passwords do not match",
    legalTitle: "TERMS, PRIVACY & WAIVER",
    legalAgreement: "FLIGHT DECK CPL EXAM PREP — LEGAL AGREEMENT",
    legalIntro:
      "Please read the following summary before creating your account. Full versions of each document are available at the links below. By ticking the checkbox you confirm that you have read, understood, and agree to all terms. Effective date: April 2, 2026 · Governing law: Province of Ontario, Canada.",
    tosTitle: "TERMS OF SERVICE",
    tos1: "By registering you agree to use this Service only for personal, non-commercial exam preparation. You must be at least 16 years of age. Account access requires administrator approval and may be revoked at any time.",
    tos2: "You agree not to share, resell, or distribute access or content; not to copy or scrape the question bank; not to post unlawful, harassing, or defamatory content; and not to attempt to compromise the security of the platform.",
    tos3: "All content — including questions, explanations, and design — is owned by Flight Deck CPL Exam Prep and is protected under Canadian copyright law. You receive a limited, non-transferable, revocable licence for personal study use only.",
    tos4: "The Service is provided \"as is.\" We reserve the right to modify or discontinue any feature at any time. We may update these Terms at any time; continued use constitutes acceptance. Disputes are subject to the exclusive jurisdiction of the courts of the Province of Ontario.",
    privacyTitle: "PRIVACY POLICY (PIPEDA)",
    privacy1: "We collect the minimum information required to operate this Service: your name, email address, and (optionally) province, date of birth, gender, pilot licence type, and ratings held. We also record your study session performance data.",
    privacy2: "We do not sell, rent, or trade your personal information. We do not use advertising trackers or third-party analytics. Passwords are stored as secure Argon2id hashes — we never have access to your password in plain text. All data is transmitted over TLS encryption.",
    privacy3: "We retain your data for as long as your account is active. You may request access, correction, or deletion of your personal information at any time through our",
    privacy3b: "support form",
    privacy3c: ". Deletion requests are processed within 30 days.",
    privacy4: "Under PIPEDA you have the right to access your data, correct inaccuracies, withdraw consent, and file a complaint with the Office of the Privacy Commissioner of Canada (priv.gc.ca) if you believe your information has been mishandled.",
    waiverTitle: "DISCLAIMER & LIABILITY WAIVER",
    waiver1: "Flight Deck CPL Exam Prep is an independent study aid. It is not affiliated with, endorsed by, or approved by Transport Canada, the Government of Canada, or any aviation regulatory authority.",
    waiver2: "Practice questions are created independently and are not official Transport Canada examination content. We make no warranty — express or implied — that content is accurate, current, complete, or reflective of current Canadian Aviation Regulations (CARs). Content may not reflect the most recent regulatory updates.",
    waiver3: "You expressly assume all risks associated with using this platform, including the risk that questions may contain errors, that use of this platform may not prepare you for the official examination, and the risk of failing an official examination. You remain solely responsible for consulting current official Transport Canada publications and seeking qualified professional instruction as part of your preparation.",
    waiver4: "To the fullest extent permitted by Canadian law, you release, waive, and discharge Flight Deck CPL Exam Prep and its operators from any and all claims arising from your use of the Service, including examination failure, inaccurate content, loss of examination fees, or loss of career opportunity.",
    waiver5: "Nothing in this Waiver limits liability for gross negligence, willful misconduct, fraud, or any matter that cannot be lawfully excluded under Canadian law. This Waiver is governed by the laws of the Province of Ontario.",
    fullDocs: "Full documents:",
    termsLink: "Terms of Service",
    privacyLink: "Privacy Policy",
    waiverLink: "Liability Waiver",
    scrollHint: "scroll to read all ↑",
    agreeCheckbox: "I have read and agree to the Terms of Service, Privacy Policy, and Liability Waiver above. I understand this platform is not affiliated with Transport Canada, provides no warranty on content accuracy, and that I use it entirely at my own risk.",
    submitting: "SUBMITTING...",
    createAccount: "CREATE ACCOUNT",
    successTitle: "REQUEST SUBMITTED",
    successBody: "Your account request has been received. You will be able to log in once the admin approves your account.",
    backToLogin: "BACK TO LOGIN",
    alreadyHaveAccess: "Already have access?",
    signIn: "Sign In",
    registrationFailed: "Registration failed",
    passwordRequirements: "Password does not meet all requirements",
    orDivider: "or",
    googleLegalHint: "Accept the agreement above to use Google sign-up.",
    googleOAuthFallback: "Sign up with Google (browser redirect)",
  },

  dashboard: {
    title: "Flight Overview",
    subtitle: "Track your situational awareness before final approach.",
    tackleturb: "Tackle Turbulence",
    openSessionHistory: "Session history",
    openSessionHistoryPanelCta: "Open full history panel",
    sessionHistoryMobileHint: "Review completed sessions and accuracy breakdown in a slide-out panel.",
    collapsibleProgressTitle: "Progress milestones",
    collapsibleProgressSub: "Tap to show or hide milestone progress on this page.",
    openLeaderboard: "Rankings",
    requestTakeoff: "Request Takeoff",
    freePlanTitle: "Free Plan — GK section only, max 20 questions/session",
    freePlanSub: "Pro Plus Advanced (CA$24.99) is the complete exam prep solution — 5,000+ questions, real CPAER-style simulations, weakness training, and an Exam Readiness Score with pass prediction.",
    proPlanTitle: "Pro Plan — practice only, ~400 questions",
    proPlanSub: "Your current plan is not sufficient for full exam preparation. Upgrade to Pro Plus Advanced (CA$24.99) for the complete 5,000+ question bank, CPAER-style timed exams, weakness training, and your Exam Readiness Score.",
    viewPlans: "View Plans",
    passYourExam: "Pass Your Exam",
    waypointsCleared: "Waypoints Cleared",
    accuracy: "Accuracy",
    flightsLogged: "Flights Logged",
    currentStreak: "Current Streak",
    days: "days",
    groundStatus: "Ground Status",
    groundStatusSub: "Hangar inventory check",
    hangarCapacity: "Hangar Capacity",
    dispatchStatus: "Dispatch Status",
    clearedToFly: "CLEARED TO FLY",
    grounded: "GROUNDED",
    goToImport: "Go to Import",
    sectorMap: "Sector Map",
    sectorMapSub: "Radar scan — accuracy and coverage across every exam domain.",
    proPlus: "PRO PLUS ADVANCED",
    avgAccuracy: "Avg Accuracy",
    bankCoverage: "Bank Coverage",
    sectorsScanned: "Sectors Scanned",
    strongestSector: "Strongest Sector",
    weakestSector: "Weakest Sector",
    deepRadarTitle: "Deep Radar Scan",
    deepRadarBadge: "PRO PLUS ADVANCED FEATURE",
    deepRadarBody: "Full per-sector accuracy, bank coverage, signal strength, and performance grading across all exam domains are exclusive to Pro Plus Advanced.",
    deepRadarProNote: "Your Pro plan covers overall progress. Upgrade to unlock the full sector intelligence view.",
    upgradeBtn: "Upgrade to Pro Plus Advanced",
    upgradePrice: "CA$19.99 / month · Cancel anytime",
    coverageLabel: "COVERAGE",
    accuracyLabel: "ACCURACY",
    remainingLabel: "remaining",
    noDataTitle: "RADAR OFFLINE",
    noDataBody: "No flight data on record. Complete your first session to begin tracking.",
    startFirstSession: "Start First Session",
    examReadiness: "Exam Readiness",
    examReadinessSub: "Projected pass rate based on practice performance.",
    examReadinessLocked: "Exam Readiness Score is a Pro Plus Advanced feature.",
    examReadinessNote: "Needs at least 100 answered questions per category.",
    unlockExam: "Unlock Exam Readiness",
    statusCleared: "CLEARED",
    statusOnCourse: "ON COURSE",
    statusMarginal: "MARGINAL",
    statusTurbulence: "TURBULENCE",
    statusCritical: "CRITICAL",
    statusUncharted: "UNCHARTED",
    attemptedLabel: "Attempted",
    correctLabel: "Correct",
    inBankLabel: "In Bank",
    signalLabel: "SIGNAL",
    accuracyWord: "accuracy",
    qNotYetAttempted: "{n} question not yet attempted",
    qNotYetAttemptedPlural: "{n} questions not yet attempted",
    waypointsClearedNote: "Total questions you have answered across completed and in-progress training sessions on this track.",
    accuracyNote: "Overall correct-answer percentage on this track: correct answers divided by total answered questions.",
    flightsLoggedNote: "Total number of completed sessions in this track.",
    currentStreakNote: "How many consecutive days you have completed at least one session in this track.",
  },

  practice: {
    title: "Pre-Flight Briefing",
    subtitle: "Configure your training session before departure.",
    sessionMode: "SESSION MODE",
    questionCount: "QUESTION COUNT",
    categoryFocus: "CATEGORY FOCUS",
    selectCategory: "Select a category",
    modeRandom: "Random Mix",
    modeRandomDesc: "Questions from all categories in random order",
    modeCategory: "Category Focus",
    modeCategoryDesc: "Deep dive into one specific exam domain",
    modeWeakness: "Weakness Training",
    modeWeaknessDesc: "Focus on your weakest areas",
    modeExam: "Exam Simulation",
    modeExamDesc: "Full CPAER-style timed exam",
    modeLadder: "Climb the Ladder",
    modeLadderDesc: "One subject: easy questions first, then harder ones",
    modeLadderFormatNote:
      "Choose one subject below, then pick how many questions you want. This mode is for learning pace — not a scored exam.",
    modeLadderPanelTitle: "HOW CLIMB THE LADDER WORKS",
    modeLadderPanelBody1:
      "All items come from the subject you select. The session orders them by difficulty: easier questions come first, then medium, then the hardest. That way you warm up before the toughest items. If a question has no difficulty tag in the bank, it is treated as medium.",
    modeLadderPanelBody2:
      "Within each difficulty band, order is shuffled so every session is not identical. On Pro and Pro Plus, the bank may prefer questions you have not seen yet while still keeping easy → medium → hard overall.",
    modeScenario: "Scenario Training",
    modeScenarioDesc: "Multi-topic, exam-style flight scenarios",
    missionParams: "Mission Parameters",
    missionParamsDesc: "Select your heading and fuel load before we spin up the engines.",
    trainingMode: "Training Mode",
    selectSubject: "Select Subject",
    selectSubjectPlaceholder: "Choose a category",
    commenceSession: "Commence Session",
    enterScenario: "Enter the Scenario",
    proceedToBriefing: "Proceed to Briefing",
    examLocked: "Exam Locked",
    initializing: "Initializing...",
    examBriefingBody: "You are about to begin a full CPL written exam simulation. This consists of {questions} questions with a {hours}-hour time limit. No answer feedback is given during the exam — results are shown at the end.",
    beginExamination: "Begin Examination",
    cancel: "Cancel",
    requestTakeoff: "REQUEST TAKEOFF",
    loadingCategories: "Loading categories...",
    categoryRequired: "Category Required",
    categoryRequiredDesc: "Please select a category to begin.",
    examBriefingTitle: "EXAM BRIEFING",
    examBriefingClosed: "EXAM BRIEFING — CLOSED",
    examCriteria: "CPAIR EXAM CRITERIA",
    ready: "READY",
    notReady: "NOT READY",
    noScenarios: "No scenario questions are available yet. Check back soon.",
    proRequired: "Pro Plus Advanced required",
    noScenarioTitle: "No Scenario Questions Available",
    noScenarioDesc: "No scenario questions have been published yet. Check back soon.",
    sessionFailTitle: "Failed to start session",
    sessionFailDesc: "Ensure you have enough questions in the bank.",
    aiScenarioLabel: "SCENARIO TRAINING",
    aiScenarioDesc: "Curated Canadian CPL scenario questions that combine topics the way the exam does. Each item includes a full explanation and a rule-of-thumb shortcut.",
    scenarioBullet1: "Navigation & wind triangle",
    scenarioBullet2: "Fuel burn calculations",
    scenarioBullet3: "Dead reckoning & groundspeed",
    scenarioBullet4: "CARs applied in-flight",
    scenarioBullet5: "Time-speed-distance problems",
    scenarioBullet6: "Drift correction angles",
    scenarioCPAERNote: "Questions follow CPAER style — slightly tricky, require thinking, not just recall.",
    examFormatNote: "Transport Canada official exam format — mixed questions from all subjects. No answer feedback during the exam.",
    examQuestionsLabel: "Questions",
    examTimeLimitLabel: "Time Limit",
    examPassMarkLabel: "Pass Mark",
    sectionReadiness: "Section Readiness",
    minCorrectEach: "Min. {n} correct each",
    correctLabel: "correct",
    allSectionsReady: "All sections ready",
    sectionsNeedPractice: "{n} section{s} need more practice",
    questionCountLabel: "Question Count",
    selectCount: "Select count",
    examUnlockedLabel: "✓ EXAM UNLOCKED",
    examLockedLabel: "🔒 LOCKED",
    freePlanNote: "Basic plan: max 40 questions per section, non-rotational.",
    upgradeForMore: "Upgrade for more.",
    examModalTitle: "EXAM LOCKED",
    examModalSubtitle: "Complete practice requirements to unlock",
    examModalBody: "Answer at least {n} questions correctly in every section before sitting the exam.",
    moreCorrectNeeded: "more correct needed",
    correctlyAnsweredCount: "correctly answered",
    close: "Close",
  },

  session: {
    question: "Question",
    of: "of",
    correct: "Correct",
    incorrect: "Incorrect",
    next: "Next Question",
    finish: "Finish Session",
    explanation: "Explanation",
    ruleOfThumb: "Rule of Thumb",
    source: "Source",
    bookmark: "Bookmark",
    bookmarked: "Bookmarked",
    reveal: "Reveal Answer",
    scenarioContext: "Scenario Context",
    examTitle: "CPL WRITTEN EXAM",
    noFeedback: "NO FEEDBACK",
    waypoints: "waypoints",
    skippedBadge: "SKIPPED",
    skippedCount: "skipped",
    abort: "ABORT",
    abortTitle: "ABORT MISSION?",
    abortBodyAnswered: "You have answered {n} of {total} questions. Your progress will be saved.",
    abortBodyNone: "You have not answered any questions yet. The session will be recorded with 0 answers.",
    continueSession: "CONTINUE SESSION",
    confirmAbort: "CONFIRM ABORT",
    save: "SAVE",
    saved: "SAVED",
    skipAnswerLater: "SKIP — ANSWER LATER",
    submitting: "SUBMITTING...",
    submitFinishExam: "SUBMIT & FINISH EXAM",
    submitAnswer: "SUBMIT ANSWER",
    confirmAnswer: "CONFIRM ANSWER",
    completeSession: "COMPLETE SESSION",
    nextWaypoint: "NEXT WAYPOINT",
    affirmative: "AFFIRMATIVE",
    negative: "NEGATIVE",
    filingFlightPlan: "FILING FLIGHT PLAN...",
    backToBriefing: "Back to Briefing Room",
    noQuestions: "ERR: NO QUESTIONS IN SESSION",
    flagQuestion: "Flag",
    flagTitle: "FLAG THIS QUESTION",
    flagDesc: "Found an error, ambiguity, or outdated content? Let us know — this is a report to our team, not a save button.",
    flagNotePlaceholder: "Describe the issue (5 words minimum)...",
    flagSubmit: "SUBMIT FLAG",
    flagSubmitting: "SUBMITTING...",
    flagSuccess: "Flag submitted",
    flagSuccessDesc: "Our team will review this question.",
    flagError: "Failed to submit flag",
    flagMinWords: "Please describe the issue in at least 5 words.",
    flagAlready: "Already flagged",
  },

  weaknesses: {
    title: "Turbulence Zones",
    subtitle: "Your most challenging questions — drill them until they're cleared.",
    tabWeakness: "Weak Questions",
    tabSaved: "Saved Questions",
    drillAll: "Drill All Weak Questions",
    flyIntoTurbulence: "Fly Into the Turbulence",
    drillSingle: "Drill",
    noWeaknessTitle: "ALL CLEAR",
    noWeaknessBody: "No weak questions detected. Keep flying — weaknesses will appear here once you answer at least 3 questions incorrectly.",
    noSavedTitle: "NO SAVED QUESTIONS",
    noSavedBody: "Bookmark questions during a session to review them here.",
    removeSaved: "Remove",
    attempts: "attempts",
    accuracy: "accuracy",
    missed: "times missed",
    proRequired: "Pro Plus Advanced Required",
    proRequiredBody: "Turbulence Zones are a Pro Plus Advanced feature. Upgrade to unlock targeted weakness training.",
    upgradeBtn: "Upgrade to Pro Plus Advanced",
    noSavedToastTitle: "No Saved Questions",
    noSavedToastDesc: "Bookmark questions during a practice session first.",
    bookmarkRemovedTitle: "Bookmark removed",
    bookmarkRemovedDesc: "Question removed from your saved list.",
    clearOneMore: "1 more correct to clear zone",
    clearTwoMore: "Answer correctly 2× to clear",
    flyThis: "Fly This",
    clearSkiesTitle: "Clear Skies Ahead",
    clearSkiesBody: "No turbulence detected yet. Keep flying more missions to identify the sectors that need work.",
    starting: "Starting...",
    drillAllN: "Drill All {n}",
    drillNSaved: "Drill {n} Saved",
    bookmarksNote: "Bookmarks are stored on this device. Completing a session does not remove them — remove individually below or inside any session.",
    bookmarkedDuring: "Bookmarked during practice",
    savedEmptyTitle: "No Saved Questions",
    savedEmptyBody: "Bookmark questions during practice sessions and drill them as a focused set.",
    bookmarksPersistNote: "Bookmarks are stored on this device and persist between sessions until you remove them.",
    pausedTitle: "Account Paused",
    pausedBody: "Turbulence Zones are not available while your account is paused.",
    pausedResumesOn: "Your account will automatically resume on {date}.",
    pausedSafe: "Your progress is safe. All other areas of Flight Deck are still accessible.",
    errorRateLabel: "Error Rate",
    wrongLabel: "Wrong",
    totalCountLabel: "Total",
    masteryLabel: "Mastery",
    weaknessLockTitle: "Weakness Training",
    weaknessLockBadge: "PRO PLUS ADVANCED FEATURE",
    weaknessLockBody: "Weakness tracking identifies the questions you miss most often and lets you drill them directly — the most efficient way to close knowledge gaps before exam day.",
    weaknessProNote: "Your current Pro plan does not include weakness tracking. Upgrade to Pro Plus to unlock it.",
    savedLockTitle: "Saved Questions",
    savedLockBadge: "PRO FEATURE",
    savedLockBody: "Bookmark questions during practice sessions and drill them as a focused set. Available on the Pro plan and above.",
    upgradeToPro: "Upgrade to Pro",
  },

  cadors: {
    pageTitle: "CADORS — daily cases",
    pageSubtitle:
      "Read occurrence narratives for the Transport Canada reporting date you select. Data is fetched live from the official CADORS national report.",
    tabCases: "Cases",
    tabAbout: "Source & disclaimer",
    dateWindowHint: "Dates are UTC calendar days. You can only pick between one month ago and today — not future dates.",
    reportingDateLabel: "Reporting date (UTC calendar day)",
    dateFromLabel: "From (UTC)",
    dateToLabel: "Through (UTC)",
    accidentsDateRangeIntro:
      "Pick one day (same from and through) or a range (e.g. the 8th through the 12th). Only accidents are returned; other types are skipped.",
    rangeSummary: "Reporting dates (UTC): {from} → {to}",
    reportingDateLabelShort: "CADORS reporting date",
    rangeInvalidTitle: "Invalid date range",
    rangeInvalidDesc: "“Through” must be on or after “From”, and both must stay within the allowed window.",
    load: "Load report",
    loading: "Fetching from Transport Canada…",
    emptyTitle: "No cases this day",
    emptyBody:
      "Nothing was returned for that reporting date, or the report had no occurrence blocks. Try another date or open the official report in a new tab.",
    errorDisabled: "CADORS study is not enabled (server flag CADORS_FEATURE or frontend VITE_ENABLE_CADORS).",
    errorGeneric: "Could not load the CADORS report from this app. Try again later or use the official Transport Canada site.",
    openOfficial: "Open official report",
    categoriesLabel: "Categories",
    typeLabel: "Occurrence type",
    filterLabel: "Show",
    filterAll: "All types",
    filterAccidents: "Accidents only",
    filterAccidentsHelp:
      "Uses Transport Canada’s occurrence type field: only rows labeled “Accident” are included (not incident, serious incident, or other types).",
    disclaimer:
      "CADORS information is preliminary and may change. The Transportation Safety Board of Canada is the official source for aviation accident and incident data. This view summarizes public CADORS narratives only and does not replace official investigation material.",
    truncated:
      "Listed {shown} of {total} that match this filter (cap {max} per request). Open the full report on the Transport Canada website for the rest.",
    occurrenceCount: "{n} case(s) listed below",
    matchLine: "{shown} listed · {total} matched this filter",
    emptyAccidentsTitle: "No accidents in this excerpt",
    emptyAccidentsBody:
      "There may still be incidents and other occurrence types on this reporting date. Switch to “All types”, pick another date, or open the full official report.",
  },

  analytics: {
    title: "Flight Logbook",
    subtitle: "Your complete training history and performance metrics.",
    currentStreak: "Current Streak",
    longestStreak: "Longest Streak",
    totalStudyDays: "Study Days",
    sessionsCompleted: "Sessions",
    passRate: "Pass Rate",
    avgPerSession: "Avg / Session",
    activityHeatmap: "Activity Heatmap",
    accuracyTrend: "Accuracy Trend",
    sessionBreakdown: "Session Breakdown",
    byCategory: "By Category",
    recentSessions: "Recent Sessions",
    noData: "No data yet. Complete your first session to start tracking.",
    proRequired: "Pro Plus Advanced Required",
    proRequiredBody: "Full flight logbook analytics are a Pro Plus Advanced feature.",
    upgradeBtn: "Upgrade to Pro Plus Advanced",
    days: "days",
    day: "day",
    modePractice: "Practice",
    modeExamSim: "Exam Sim",
    modeWeakness: "Weakness",
    modeCategory: "Category",
    consecutiveDays: "consecutive days",
    personalBest: "personal best",
    totalDaysActive: "total days active",
    sessionsDone: "sessions done",
    activityTitle: "30-DAY ACTIVITY",
    sessionModesTitle: "SESSION MODES",
    accuracyTrendTitle: "ACCURACY TREND",
    lastSessions: "last 20 sessions",
    categoryAccTitle: "CATEGORY ACCURACY",
    noActivityYet: "No activity yet.",
    noSessionsYet: "No sessions yet.",
    lockedCurrentStreak: "Current Streak",
    lockedLongestStreak: "Longest Streak",
    lockedStudyDays: "Study Days",
    lockedPassRate: "Pass Rate",
    lockedAvgQ: "Avg Q/Session",
    lockedCompleted: "Completed",
    todayLabel: "today",
    answeredLabel: "answered",
    recentSessionsTitle: "RECENT SESSIONS",
    tableDate: "Date",
    tableMode: "Mode",
    tableTrack: "Track",
    tableCategory: "Category",
    tableQuestions: "Questions",
    tableAccuracy: "Accuracy",
    noAnswersYet: "No answers recorded yet.",
    noSessionsCompleted: "No completed sessions yet.",
    unlockAnalytics: "Unlock Advanced Analytics",
    unlockAnalyticsDesc: "Upgrade to Pro Plus Advanced for session trends, activity heatmap, category accuracy breakdown, and more.",
    activityToday: "Today",
    activityLess: "Less",
    activityMore: "More",
    lockedTitle: "PRO PLUS ADVANCED",
    lockedBody: "Detailed analytics are part of the Pro Plus Advanced plan (CA$19.99/mo). Upgrade to unlock session trends, activity heatmap, category accuracy breakdown, and more.",
    upgradeNow: "Upgrade Now",
    noSessionsChart: "No sessions yet.",
    currentStreakNote: "Current number of consecutive days with at least one completed session.",
    longestStreakNote: "Your best all-time consecutive-day study streak.",
    totalStudyDaysNote: "Total unique calendar days where you completed at least one session.",
    passRateNote: "Percentage of completed sessions that finished with at least 70% accuracy.",
    avgPerSessionNote: "Average number of answered questions per completed session.",
    sessionsCompletedNote: "Total number of sessions you have fully completed.",
  },

  sessionHistory: {
    title: "Session review",
    subtitle: "Every completed test at a glance — totals, misses, and skips — with a shortcut back into the debrief.",
    infoHint: "Counts are based on your saved answers. “Unanswered” means questions in that session you did not submit an answer for before finishing.",
    shuffle: "Shuffle order",
    summaryTitle: "All loaded sessions",
    colAll: "All",
    colUnanswered: "Unanswered",
    colIncorrect: "Incorrect",
    colCorrect: "Correct",
    reviewCta: "Review",
    accuracyShort: "accuracy",
    trackLabel: "Track",
    allCategories: "All categories",
    empty: "No completed sessions yet. Finish a practice or exam run to build your history here.",
    goPractice: "Go to practice",
    loadError: "Could not load session history.",
    modeRandom: "Random mix",
    modeCategory: "Category",
    modeWeakness: "Weakness",
    modeExam: "Exam sim",
    modeScenario: "Scenario",
  },

  leaderboard: {
    title: "Pilot rankings",
    subtitle: "Pilots are ranked by how well they performed in the period you pick—right answers, mistakes, and overall accuracy on this track. Only nicknames are shown.",
    metricHelp: "Rolling windows (UTC): 7 days, 30 days, or all time. Only pilots with a nickname who opted in are listed.",
    sortHint: "Higher ranks favor strong accuracy with a healthy number of correct answers—not who tapped the most questions. Same idea for every time window.",
    tabWeek: "7 days",
    tabMonth: "30 days",
    tabAll: "All time",
    podiumTitle: "Top pilots",
    podium1st: "1st",
    podium2nd: "2nd",
    podium3rd: "3rd",
    fullStandings: "Full standings",
    colRank: "#",
    colPilot: "Pilot",
    colRankScore: "Score",
    colCorrect: "✓",
    colWrong: "✗",
    colScore: "Correct",
    colAttempts: "Attempts",
    colAccuracy: "Acc.",
    you: "you",
    yourRank: "Your position",
    correctLabel: "correct",
    wrongLabel: "wrong",
    attemptsLabel: "attempts",
    accuracyLabel: "accuracy",
    notRanked: "You are not on this board yet — set a nickname, stay opted in, and answer questions to appear.",
    empty: "No rankings for this period yet. Be the first on the board.",
    loadError: "Could not load rankings.",
    retry: "Try again",
  },

  milestones: {
    title: "Progress milestones",
    mobileOpenHint: "Tap to open details",
    subtitle:
      "Targets are computed from your real sessions and answers on this licence track — no pop-ups, no arcade noise. Check back as you study; bars update automatically.",
    unlockedSummary: "{n} of {total} unlocked",
    badgeStrip: "Recognition badges",
    done: "Done",
    loadError: "Could not load milestones. Your study data is still safe — try refreshing.",
    footer:
      "Tip: short, steady sessions beat rare cramming. These milestones reward consistency and depth, the same habits that carry you through exam week.",
    items: {
      first_finish: {
        title: "First mission complete",
        desc: "Finish at least one full practice or exam session on this track.",
      },
      week_rhythm: {
        title: "Week rhythm",
        desc: "Log practice on at least 3 different calendar days (UTC) in the last 7 days.",
      },
      streak_3: {
        title: "Three-day streak",
        desc: "Build a 3-day consecutive streak with completed sessions on this track (UTC days).",
      },
      century_correct: {
        title: "Century club",
        desc: "Bank 100 correct answers on this track — proof the question bank is working for you.",
      },
      five_hundred: {
        title: "Five hundred strong",
        desc: "Reach 500 correct answers on this track — serious volume toward exam readiness.",
      },
      category_explorer: {
        title: "Category explorer",
        desc: "Touch at least 4 categories with 5+ answers each — balanced prep, fewer blind spots.",
      },
      monthly_momentum: {
        title: "Monthly momentum",
        desc: "Complete 10 sessions on this track in the last 30 days — sustainable pace.",
      },
      accuracy_ready: {
        title: "Accuracy gate",
        desc: "At least 80 answers with ≥72% overall accuracy on this track — quality over rush.",
      },
    },
  },

  review: {
    examDebrief: "Exam Debrief",
    debriefing: "Debriefing",
    errNotFound: "ERR: SESSION DATA NOT FOUND",
    pass: "PASS",
    fail: "FAIL",
    partialFail: "PARTIAL\nFAIL",
    correctOutOf: "{n} correct out of {total} questions (overall)",
    partialFailNoteOne: "Overall score qualifies ({score}% ≥ {pass}%), but the {cat} section did not meet the required {pass}% minimum. Every section must pass.",
    partialFailNoteMany: "Overall score qualifies ({score}% ≥ {pass}%), but {n} sections did not meet the required {pass}% minimum. Every section must pass.",
    failNote: "You needed {pass}% overall to pass. You scored {score}% — {short} mark{s} short.",
    failNoteOne: "You needed {pass}% overall to pass. You scored {score}% — {short} mark short.",
    sectionBreakdown: "Section Breakdown",
    minRequired: "Minimum required per section: {pass}%",
    flyAgain: "Fly Again",
    goAround: "Go Around",
    newFlight: "New Flight",
    flightScore: "Flight Score",
    yourCall: "Your Call:",
    correctLabel: "Correct:",
    explanation: "EXPLANATION:",
    examReview: "Exam Review",
    flightLog: "Flight Log",
    records: "RECORDS",
    practiceLabel100: "Perfect Approach — Greased It",
    practiceLabel90: "Outstanding — Almost Faultless",
    practiceLabel80: "Solid Flying — Good Approach",
    practiceLabel70: "Cleared — Tighten the Circuit",
    practiceLabelFail: "Go Around — More Circuits Needed",
    answeredCorrectly: "You answered {n} correctly out of {total} total questions.",
    saveQuestion: "Save",
    savedQuestion: "Saved",
    unsaveQuestion: "Remove save",
  },

  scenarioTraining: {
    title: "Scenario Training",
    subtitle: "Exam-style CPL flight scenarios for focused practice.",
    selectTier: "SELECT YOUR TIER",
    selectTierSub: "Choose a difficulty level to begin your scenario session.",
    startSession: "START SESSION",
    question: "Question",
    of: "of",
    score: "Score",
    correct: "Correct",
    incorrect: "Incorrect",
    next: "Next Question",
    sessionComplete: "SESSION COMPLETE",
    accuracy: "Accuracy",
    restart: "Start New Session",
    backToTiers: "Back to Tiers",
    explanation: "Explanation",
    ruleOfThumb: "Rule of Thumb",
    scenarioContext: "Scenario Context",
    proRequired: "Pro Plus Advanced Required",
    proRequiredBody: "Scenario Training is an exclusive Pro Plus Advanced feature. Upgrade to unlock scenario-style sessions.",
    upgradeBtn: "Upgrade to Pro Plus Advanced",
    loadingSession: "Loading your scenario session...",
    noQuestionsTitle: "NO SCENARIOS AVAILABLE",
    noQuestionsBody: "No scenario questions are available for this tier yet. Please try another tier.",
    tierStandard: "Standard",
    tierIntermediate: "Intermediate",
    tierAdvanced: "Advanced",
    tierExpert: "Expert",
    available: "available",
    noQuestionsYet: "No questions yet",
    aiDisclaimer: "Scenario questions are authored and curated by our team. They may differ in style from the main rotating bank and appear only in this training mode.",
    proRequiredUpgrade: "Upgrade to access Scenario Training.",
    seeResults: "See Results",
    outstanding: "Outstanding!",
    goodWork: "Good work",
    keepPracticing: "Keep practicing",
    summaryAnsweredCorrectly: "You answered {correct} of {total} questions correctly",
    totalLabel: "Total",
  },

  settings: {
    title: "Settings",
    tabs: {
      appearance: "Appearance",
      account: "Profile",
      security: "Security",
      billing: "Billing",
    },
    appearance: {
      title: "Appearance",
      themeLabel: "Theme",
      languageLabel: "Language",
      languageDesc: "Choose your preferred language for the interface.",
      fontSizeLabel: "Text size",
      fontSizeDesc: "Adjust how large question text and menus appear on your device.",
      fontSizes: {
        xs: "Extra small",
        sm: "Small",
        md: "Default",
        lg: "Large",
        xl: "Extra large",
      },
      fontStyleLabel: "Font style",
      fontStyleDesc: "Theme default uses each theme's built-in fonts. Other options replace them everywhere.",
      fontStyles: {
        theme: "Theme default",
        modern: "Modern",
        system: "System",
        mono: "Monospace",
        serif: "Serif",
        classic: "Classic",
      },
      reduceMotionLabel: "Reduce animations",
      reduceMotionDesc: "Less motion across the app — calmer transitions and effects.",
      comfortableSessionLabel: "Spacious practice layout",
      comfortableSessionDesc: "Larger answer buttons and more spacing during questions (practice, session, and review).",
      sidebarClockLabel: "Sidebar clock",
      sidebarClockDesc: "Control the clock shown in the navigation sidebar and mobile header.",
      showSidebarClockLabel: "Show clock in sidebar",
      showSidebarClockDesc: "Turn off to hide the clock from the sidebar and mobile header.",
      localTimeLabel: "Use local time",
      localTimeDesc: "When on, shows your device time zone instead of UTC (Zulu).",
    },
    account: {
      title: "Account",
      profileTitle: "Pilot Profile",
      nicknameLabel: "Nickname (rankings)",
      nicknamePlaceholder: "e.g. Skyhawk_42",
      nicknameHint: "Public name on the leaderboard. 2–24 characters: letters, numbers, spaces, underscore, or hyphen. No profanity, slurs, or offensive language. Leave empty to stay off the board.",
      leaderboardOptInLabel: "Show me on the rankings",
      leaderboardOptInHint: "Turn off to hide your scores from the public list (your nickname is never shown while off).",
      fullName: "Full Name",
      email: "Email",
      phone: "Phone",
      country: "Country",
      pilotType: "Pilot Type / Licence",
      ratings: "Ratings & Endorsements",
      saveProfile: "Save Profile",
      saving: "Saving...",
      saved: "Saved",
      securityTitle: "Security",
      changePassword: "Change Password",
      twoFaTitle: "Two-Factor Authentication",
    },
    billing: {
      title: "Billing",
      currentPlan: "Current Plan",
      manageSub: "Manage Subscription",
      cancelSub: "Cancel Subscription",
      renewSub: "Renew Subscription",
    },
    headerSub: "Manage your account, appearance, and subscription",
    recovery: {
      title: "Recovery Codes",
      hint: "Save these codes somewhere safe — a password manager, printed paper, or secure note. Each code can only be used once. These codes will not be shown again.",
      copyAll: "Copy All",
      download: "Download",
      savedCodes: "I've saved my codes",
    },
  },

  contact: {
    title: "Tower",
    subtitle: "Submit a support ticket, track your conversations, or read messages from the admin team.",
    tabNew: "New Ticket",
    tabMine: "My Tickets",
    tabInbox: "Inbox",
    tabNewsletter: "Newsletter",
    formTitle: "Submit a Support Ticket",
    formSubtitle: "We typically respond within 24–48 hours.",
    categoryLabel: "Category",
    categoryPlaceholder: "Select a category...",
    subjectLabel: "Subject",
    subjectPlaceholder: "Brief summary of your issue or question",
    messageLabel: "Message",
    messagePlaceholder: "Describe your issue in as much detail as possible. If it's a content question, include the question number (shown as Q# during a session).",
    submitBtn: "SUBMIT TICKET",
    submitting: "SUBMITTING...",
    validationCategory: "Select a category",
    validationSubject: "Subject too short",
    validationMessage: "Message too short (min 10 chars)",
    successTitle: "TICKET SUBMITTED",
    successBody: "We've received your message and will reply as soon as possible. You can track the conversation in My Tickets.",
    submitAnother: "Submit Another",
    viewMyTickets: "View My Tickets",
    noTickets: "No tickets yet.",
    noArchived: "No archived tickets.",
    statusOpen: "OPEN",
    statusInProgress: "IN PROGRESS",
    statusResolved: "RESOLVED",
    replyPlaceholder: "Type your reply...",
    sendReply: "Send",
    sending: "Sending...",
    archiveBtn: "Archive",
    unarchiveBtn: "Unarchive",
    deleteBtn: "Delete",
    confirmDelete: "Are you sure you want to delete this ticket?",
    cancel: "Cancel",
    noMessages: "No messages yet.",
    noNewsletters: "No newsletters yet.",
    draft: "Draft",
    recipients: "recipients",
    failedSubmit: "Failed to submit. Please try again.",
    showArchived: "Show Archived",
    hideArchived: "Hide Archived",
    threadOriginalMsg: "Your original message",
    threadLoading: "Loading conversation...",
    threadNoReplies: "No replies yet — we'll get back to you soon.",
    threadReplyHint: "Add more information… (Ctrl+Enter to send)",
    threadSending: "SENDING...",
    threadSend: "SEND",
    threadResolved: "This ticket is resolved. Submit a new ticket if you need further help.",
  },

  login: {
    tagline: "Prepare for Your Exams, Master the Skies",
    emailLabel: "Email",
    emailPlaceholder: "pilot@example.com",
    passwordLabel: "Password",
    forgotPassword: "Forgot password?",
    rememberMe: "Remember me for 30 days",
    accountLocked: "Account Locked",
    contactSupportUnlock: "Contact support to unlock",
    deviceInUse: "Device Already In Use",
    signingIn: "Signing in...",
    finalizingSignIn: "Finalizing sign-in…",
    finalizingRetry: "Try again",
    finalizingBackToLogin: "Back to login",
    logIn: "Log In",
    noAccount: "Don't have an account?",
    signUp: "Sign Up",
    privacy: "Privacy",
    terms: "Terms",
    waiver: "Waiver",
    recoveryCodeTitle: "Recovery Code",
    twoFaTitle: "Two-Factor Auth",
    emailVerification: "Email verification",
    authenticatorApp: "Authenticator app",
    enterRecoveryHint: "Enter one of your saved recovery codes.",
    enterEmailCodeHint: "Enter the 6-digit code sent to your email.",
    enterAppCodeHint: "Enter the 6-digit code from your authenticator app.",
    recoveryCodeLabel: "Recovery Code",
    verificationCodeLabel: "Verification Code",
    verifying: "Verifying...",
    verify: "Verify",
    resendCode: "Resend code",
    useRecovery: "Use a recovery code",
    useAuthEmail: "Use authenticator / email code",
    backToLogin: "Back to login",
    orDivider: "or",
    supportTitle: "Support",
    supportHint: "Having trouble? Send us a message and we'll help you out.",
    supportFullName: "Full Name *",
    supportPhone: "Phone",
    supportNamePlaceholder: "Jane Doe",
    supportPhonePlaceholder: "+1 (555) 000-0000",
    supportEmailLabel: "Email *",
    supportEmailPlaceholder: "pilot@example.com",
    supportSubject: "Subject *",
    supportSubjectPlaceholder: "Brief summary...",
    supportMessage: "Message *",
    supportMessagePlaceholder: "Describe your issue (minimum 20 characters)...",
    supportSending: "SENDING...",
    supportSend: "SEND MESSAGE",
    supportSentTitle: "MESSAGE SENT",
    supportSentBody: "We've received your message and will get back to you as soon as possible.",
    supportClose: "CLOSE",
  },

  subscription: {
    backToDashboard: "← Dashboard",
    title: "CHOOSE YOUR FLIGHT PATH",
    subtitle: "One plan is for practice. One plan is for passing.",
    subtitle2: "Only Pro Plus Advanced replicates real Transport Canada exam conditions.",
    currentPlan: "CURRENT PLAN:",
    monthly: "MONTHLY",
    annual: "ANNUAL",
    save20: "SAVE 20%",
    billedAnnually: "billed annually",
    perMonth: "/mo",
    cancelAnytime: "per month · cancel anytime",
    noCardNeeded: "No card needed · forever",
    currentPlanBadge: "YOUR CURRENT PLAN",
    downgradePortal: "DOWNGRADE VIA BILLING PORTAL",
    includedIn: "INCLUDED IN PRO PLUS ADVANCED",
    practiceOnly: "PRACTICE ONLY",
    practiceOnlyWarning: "Practice-only tier. Not sufficient for full exam preparation. Limited question pool may leave knowledge gaps.",
    included: "Included",
    notIncluded: "Not included",
    manageSubscription: "MANAGE SUBSCRIPTION",
    loading: "LOADING...",
    examReadyScore: "Exam Readiness Score tells you exactly when you're ready to book your Transport Canada exam.",
    passYourExam: "PASS YOUR EXAM",
    startPracticing: "Start Practicing",
    examReady: "EXAM READY",
    proOnlyNote: "A solid way to get familiar with exam topics using ~400 questions across all 4 sections. Suitable for early-stage studying. Not recommended as your sole preparation tool — the limited, non-rotating question pool means you will not see the full range of questions that appear on the real exam.",
    proNote: "PRO — PRACTICE ONLY",
    proPlusNote: "PRO PLUS ADVANCED — COMPLETE EXAM PREP",
    proPlusDesc: "The only tier designed to get you through the real exam. With 5,000+ questions, timed CPAER simulations, weakness drilling, and an Exam Readiness Score with pass prediction — you'll know exactly when you're ready to book.",
    footer: "Payments processed securely by Stripe. Cancel anytime from the billing portal.",
    manageBilling: "Manage billing / cancel subscription",
    designedToPass: "Designed for students who want to pass on the first attempt",
    proCompleteDesc: "The complete CPL exam preparation system. Built to replicate real Transport Canada exam conditions — so you walk in prepared, confident, and ready to pass on the first attempt.",
    errorCheckout: "Could not start checkout. Please try again.",
    errorPortal: "Could not open billing portal. Please try again.",
    mostPopular: "MOST POPULAR",
    platinumSelectPlan: "SELECT PLAN",
    platinumSelectThisPlan: "SELECT THIS PLAN",
    platinumSubscribeProPlus: "SUBSCRIBE TO PRO PLUS",
    platinumStarterLabel: "STARTER BASIC (FREE)",
    platinumCplPrepPro: "CPL PREP PRO",
    platinumProPlusTitle: "PRO PLUS ADVANCED",
    platinumAdminPreview: "ADMIN PREVIEW — showing page as a free user sees it",
  },

  onboarding: {
    welcomeTitle: "Welcome aboard, {name}!",
    welcomeBody: "Flight Deck is your CPL exam co-pilot. 5,000+ questions, focused practice modes, and an Exam Readiness Score that tells you exactly when to book. Let's walk you through it.",
    skip: "Skip Tour",
    letsGo: "Show Me Around",
    back: "Back",
    next: "Next",
    finish: "One More Thing",

    step1Title: "Mission Overview",
    step1Body: "Your dashboard is mission control. See your total accuracy, questions answered, daily streaks, and a full subject-by-subject breakdown across all 4 Transport Canada CPL exam domains — so you always know exactly where you stand.",

    step2Title: "Request Takeoff",
    step2Body: "5,000+ questions across 4 subject areas. Drill by category, use Climb the Ladder for easy-to-hard pacing in one subject, shuffle randomly, or run a full timed CPAER simulation. Every answer includes a full explanation with CARs references — so you understand the 'why', not just the correct letter.",

    step3Title: "Scenario Training",
    step3Body: "Practice real exam-style scenario questions that combine multiple topics at once — similar to what you'll face on test day. Use this mode to sharpen judgment, calculations, and decision-making under pressure.",
    step3Badge: "NEW",

    step4Title: "Turbulence Zones",
    step4Body: "The system logs every wrong answer and automatically identifies your weakest categories. Focused drills zero in on your gaps — no time wasted reviewing things you already know. Most students improve their lowest category by 30%+ within a week of targeted practice.",

    step5Title: "Flight Logbook",
    step5Body: "Your Exam Readiness Score is calculated from accuracy trends, question diversity, and consistency over time. When it holds above 80%, you're statistically ready to pass the Transport Canada written exam. Track your weekly curve and see exactly when you peaked.",

    step6Title: "Personalize Your Cockpit",
    step6Body: "Switch between English and French at any time, choose a visual theme that suits your study environment, enable two-factor authentication for account security, and manage your billing and subscription from one place. Your cockpit, your rules.",

    step7Title: "Navigation Quick Map",
    step7Body: "Use Flight Deck for your live mission view, Request Takeoff to launch sessions, Turbulence Zones to drill mistakes, Tower for support, and Settings for profile + security. If you ever feel lost, come back to Flight Deck first.",

    step8Title: "How Rankings Work",
    step8Body: "Open Settings > Profile, set a nickname, and keep leaderboard visibility enabled. Then use the Rankings button on Flight Deck. Your position reflects how you actually performed in that window—right answers, mistakes, and accuracy—not a gimmick score. Only nicknames are shown publicly.",

    step9Title: "Button Cheat Sheet",
    step9Body: "Primary buttons (filled) move you forward: start sessions, submit answers, and continue. Outline buttons open tools like Rankings and Session History. If unsure, read the small hint text under each section before tapping.",

    finalTitle: "Cleared for Takeoff — Let's Get You Certified",
    finalSubtitle: "Everything you need to pass your CPL written exam on the first attempt.",
    finalWhatsIncluded: "What's included in Pro Plus Advanced",
    upsell1: "5,000+ rotating questions across all 4 CPL exam subjects",
    upsell2: "Exam-style scenario practice to strengthen weak spots over time",
    upsell3: "Full timed CPAER exam simulations with pass/fail prediction",
    upsell4: "Exam Readiness Score — know exactly when to book your Transport Canada exam",
    upsell5: "Automatic weakness detection with targeted drill engine",
    upgradeNow: "Upgrade to Pro Plus Advanced",
    startPracticing: "Start Practicing First",
    goToDashboard: "Not now — take me to the dashboard",
    neverShow: "Don't show this again",
  },

  resetPassword: {
    subtitle: "RESET PASSWORD",
    invalidLink: "Invalid reset link. Please request a new one.",
    backToLogin: "Back to Login",
    newPasswordLabel: "NEW PASSWORD",
    confirmPasswordLabel: "CONFIRM PASSWORD",
    hint: "Min 8 chars • Upper & lowercase • Special character • No long sequential runs",
    missingToken: "Missing reset token. Please use the link from your email.",
    passwordMismatch: "Passwords do not match",
    saving: "Saving...",
    submit: "SET NEW PASSWORD",
    successTitle: "PASSWORD UPDATED",
    successBody: "Redirecting you to login...",
    resetFailed: "Reset failed",
  },

  adminPublicContacts: {
    title: "Pre-Login Contacts",
    subtitle: "Support messages submitted from the public login page before signing in.",
    markAllSeen: "Mark All Seen",
    noContacts: "No pre-login contact submissions yet.",
    colDate: "Date",
    colName: "Name / Email",
    colPhone: "Phone",
    colSubject: "Subject",
    colMessage: "Message",
    colIP: "IP",
    colStatus: "Status",
    statusNew: "NEW",
    statusSeen: "Seen",
    markSeen: "Mark Seen",
    loadingError: "Failed to load contacts.",
    markAllDone: "All contacts marked as seen.",
    expandMsg: "Show full message",
    collapseMsg: "Collapse",
  },

  common: {
    loading: "Loading...",
    error: "An error occurred. Please try again.",
    cancel: "Cancel",
    save: "Save",
    close: "Close",
    upgrade: "Upgrade",
    learnMore: "Learn More",
    backToDashboard: "Back to Dashboard",
    notFound: "Page not found",
    accuracy: "Accuracy",
    noData: "No data available",
  },
};

export type TranslationDict = Omit<typeof en, "lang"> & { lang: "en" | "fr" };
